const clampPercent = (value, fallback, maximum = 100) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return fallback;
  return Math.min(maximum, Math.max(0, numeric));
};

export const CREATOR_AUDIO_PROCESSOR_NAME = "showmelive-creator-equalizer";

export const getCreatorAudioParameters = ({
  speakerVolume = 80,
  micVolume = 80,
  balance = 50,
  treble = 50,
  bass = 50,
} = {}) => ({
  // 100% is neutral. Values above 100% are a genuine Web Audio gain boost
  // (up to +6 dB at 200%) rather than a cosmetic slider value.
  speakerGain: clampPercent(speakerVolume, 80, 200) / 100,
  microphoneGain: clampPercent(micVolume, 80, 200) / 100,
  pan: (clampPercent(balance, 50) - 50) / 50,
  trebleGainDb: (clampPercent(treble, 50) - 50) * 0.24,
  bassGainDb: (clampPercent(bass, 50) - 50) * 0.24,
});

const smoothlySet = (audioParam, value, context) => {
  if (!audioParam || !context) return;
  const now = context.currentTime;
  audioParam.cancelScheduledValues?.(now);
  if (typeof audioParam.setTargetAtTime === "function") {
    audioParam.setTargetAtTime(value, now, 0.015);
  } else {
    audioParam.value = value;
  }
};

const createAudioContext = () => {
  const AudioContextConstructor = globalThis.AudioContext || globalThis.webkitAudioContext;
  if (!AudioContextConstructor) throw new Error("Web Audio is not supported by this browser");
  try {
    return new AudioContextConstructor({ sampleRate: 48_000, latencyHint: "interactive" });
  } catch {
    return new AudioContextConstructor();
  }
};

/**
 * LiveKit TrackProcessor that keeps browser suppression disabled while applying
 * creator-controlled gain, stereo balance, and gentle two-band EQ. The graph is
 * created once; slider movement updates AudioParams without replacing the mic.
 */
export class CreatorAudioProcessor {
  constructor(settings = {}) {
    this.name = CREATOR_AUDIO_PROCESSOR_NAME;
    this.settings = settings;
    this.context = null;
    this.ownsContext = false;
    this.source = null;
    this.microphoneGain = null;
    this.bassFilter = null;
    this.trebleFilter = null;
    this.panner = null;
    this.destination = null;
    this.processedTrack = undefined;
  }

  async init({ track, audioContext }) {
    this.context = audioContext || createAudioContext();
    this.ownsContext = !audioContext;
    await this.context.resume?.().catch(() => {});

    this.microphoneGain = this.context.createGain();
    this.bassFilter = this.context.createBiquadFilter();
    this.bassFilter.type = "lowshelf";
    this.bassFilter.frequency.value = 180;
    this.trebleFilter = this.context.createBiquadFilter();
    this.trebleFilter.type = "highshelf";
    this.trebleFilter.frequency.value = 4_000;
    this.panner = this.context.createStereoPanner?.() || null;
    this.destination = this.context.createMediaStreamDestination();

    this.microphoneGain.connect(this.bassFilter);
    this.bassFilter.connect(this.trebleFilter);
    if (this.panner) {
      this.trebleFilter.connect(this.panner);
      this.panner.connect(this.destination);
    } else {
      this.trebleFilter.connect(this.destination);
    }

    await this.restart({ track });
    this.processedTrack = this.destination.stream.getAudioTracks()[0];
    if (!this.processedTrack) throw new Error("The browser did not create a processed audio track");
    if ("contentHint" in this.processedTrack) this.processedTrack.contentHint = "music";
    this.updateSettings(this.settings);
  }

  async restart({ track }) {
    this.source?.disconnect?.();
    if (!track || !this.context || !this.microphoneGain) return;
    if ("contentHint" in track) track.contentHint = "music";
    this.source = this.context.createMediaStreamSource(new MediaStream([track]));
    this.source.connect(this.microphoneGain);
    await this.context.resume?.().catch(() => {});
  }

  updateSettings(settings = {}) {
    this.settings = { ...this.settings, ...settings };
    const parameters = getCreatorAudioParameters(this.settings);
    smoothlySet(this.microphoneGain?.gain, parameters.microphoneGain, this.context);
    smoothlySet(this.bassFilter?.gain, parameters.bassGainDb, this.context);
    smoothlySet(this.trebleFilter?.gain, parameters.trebleGainDb, this.context);
    smoothlySet(this.panner?.pan, parameters.pan, this.context);
    return parameters;
  }

  async resume() {
    await this.context?.resume?.().catch(() => {});
    return this.context?.state === "running";
  }

  async destroy() {
    this.source?.disconnect?.();
    this.microphoneGain?.disconnect?.();
    this.bassFilter?.disconnect?.();
    this.trebleFilter?.disconnect?.();
    this.panner?.disconnect?.();
    this.destination?.disconnect?.();
    this.source = null;
    this.processedTrack = undefined;
    if (this.ownsContext && this.context?.state !== "closed") {
      await this.context.close?.().catch(() => {});
    }
    this.context = null;
  }
}
